import { BaseApiService } from "./baseApiService";

// Global error dialog handler that can be set by components
let globalErrorDialogHandler:
  | ((options: {
      title?: string;
      message: string;
      actionButtonText?: string;
    }) => void)
  | null = null;

export const setGlobalErrorDialogHandler = (
  handler:
    | ((options: {
        title?: string;
        message: string;
        actionButtonText?: string;
      }) => void)
    | null
) => {
  globalErrorDialogHandler = handler;
};

export interface PolicyDownloadRequest {
  agencyId: string;
  platform_org_id: string;
  policyDocument: {
    id: string;
    name: string;
  };
  latestVersion: {
    versionNumber: string;
  };
  languageCode: string;
}

export interface PolicyDownloadResponse {
  signedUrl: string;
  fileName: string;
  expiresAt?: string;
}

/**
 * Downloads a policy document by calling the appropriate API endpoint
 * @param downloadRequest - The download request parameters
 * @returns Promise<PolicyDownloadResponse>
 */
export const downloadPolicyDocument = async (
  downloadRequest: PolicyDownloadRequest
): Promise<PolicyDownloadResponse> => {
  try {
    const endpoint = "/download";

    const response = await BaseApiService.post({
      url: endpoint,
      requestData: downloadRequest,
      isJson: true,
      showModalOnError: false, // Handle errors manually to check for NO_PERMISSIONS
    });

    if (response.success && response.data) {
      return response.data as PolicyDownloadResponse;
    } else {
      // Check if this is a NO_PERMISSIONS error
      if (response.errorCode === "NO_PERMISSIONS") {
        // Show MUI modal alert for permission denied
        if (globalErrorDialogHandler) {
          globalErrorDialogHandler({
            title: "Access Denied",
            message:
              "You do not have permission to download this document. Please contact your administrator for access.",
            actionButtonText: "OK",
          });
        }
        throw new Error("NO_PERMISSIONS");
      } else {
        throw new Error(response.error || "Failed to get download URL");
      }
    }
  } catch (error) {
    console.error("Error downloading policy document:", error);
    throw error;
  }
};

/**
 * Initiates a file download from a signed URL
 * @param signedUrl - The CloudFront signed URL
 * @param fileName - The name of the file to download
 */
export const downloadFileFromUrl = async (
  signedUrl: string,
  fileName: string
): Promise<void> => {
  try {
    // Create a temporary anchor element to trigger download
    const link = document.createElement("a");
    link.href = signedUrl;
    link.download = fileName;
    link.target = "_blank";

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/**
 * Complete policy download flow - gets signed URL and downloads the file
 * @param downloadRequest - The download request parameters
 */
export const downloadPolicyDocumentComplete = async (
  downloadRequest: PolicyDownloadRequest
): Promise<void> => {
  try {
    // Get the signed URL from the API
    const downloadResponse = await downloadPolicyDocument(downloadRequest);

    // Only proceed with file download if we successfully got the signed URL
    if (downloadResponse && downloadResponse.signedUrl) {
      await downloadFileFromUrl(
        downloadResponse.signedUrl,
        downloadResponse.fileName
      );
    } else {
      throw new Error("Invalid download response - missing signed URL");
    }
  } catch (error) {
    // Don't proceed with file download if there was an error getting the signed URL
    console.error("Error in complete download flow:", error);

    // Re-throw the error so the calling component can handle it appropriately
    // (e.g., NO_PERMISSIONS errors should not trigger file download)
    throw error;
  }
};
